import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { IOverlayLoaderStore } from '../../../../../../modules/common/OverlayLoader.store';
import { Settings } from '../../../../../../modules/settings/SettingsContext';
import { useLoader } from '../../../../../helpers/useLoader';
import { SortingDirection } from '../../../../../models/sortingDirection.model';
import { IProjectHubService } from '../../../../../services/projectHub.service';
import { IAuthStore } from '../../../../auth/auth.store';
import { IFreeAccessStore } from '../../../../freeAccess/freeAccess.store';
import { IUserStore } from '../../../../user/user.store';
import { ProjectDetailsImagesList } from '../components/ProjectDetailsImagesList';
import { SortingBy } from '../imageFilters.model';
import { IProjectDetailsImagesBl } from '../projectDetailsImages.bl';
import { ImageSetType } from '../projectDetailsImages.model';
import { IProjectDetailsImagesStore } from '../projectDetailsImages.store';
import { IImageFilterService } from '../services/imageFilters.service';

export const ProjectDetailsImagesContainer = observer((): ReactElement => {
  const projectDetailsImagesBl = useInjection(IProjectDetailsImagesBl.$);
  const authStore = useInjection(IAuthStore.$);
  const userStore = useInjection(IUserStore.$);
  const overlayLoader = useInjection(IOverlayLoaderStore.$);
  const projectHubService = useInjection(IProjectHubService.$);
  const freeAccessStore = useInjection(IFreeAccessStore.$);
  const imagesStore = useInjection(IProjectDetailsImagesStore.$);
  const imageFilterService = useInjection(IImageFilterService.$);

  const settingsContext = useContext(Settings);
  const { workspaceId, projectId } = useParams<{ projectId: string; workspaceId: string }>();
  const { withLoaderAsync } = useLoader();

  useEffect(() => {
    const fetch = async (): Promise<void> => {
      const { projectId: storedProjectId, filterId } = imagesStore;
      await projectDetailsImagesBl.getImageTabDataAsync(projectId, filterId);
      await projectHubService.initializeAsync();
      if (storedProjectId !== projectId) {
        freeAccessStore.paging.checkDate = '';
      }
      await withLoaderAsync(async () => {
        await projectDetailsImagesBl.getProjectImagesAsync();
      }, 'details-images-loading');
    };

    void fetch();
  }, []);

  const handlePaginationChange = async (pageNumber: number, pageSize: number): Promise<void> => {
    await withLoaderAsync(async (): Promise<void> => {
      await imageFilterService.paginationChange(pageNumber, pageSize);
      settingsContext.setProjectDetailsImagesPageSize(pageSize);
    }, 'details-images-loading');
  };

  const handleOrderingChangeAsync = async (by: SortingBy, direction: SortingDirection | undefined): Promise<void> => {
    freeAccessStore.paging.checkDate = '';
    await withLoaderAsync(async () => {
      await imageFilterService.changeSortingAsync(by, direction);
    }, 'details-images-loading');
  };

  const handleNonBatchCheck = (id: string): void => {
    let newList = imagesStore.toggledImages.slice();
    if (newList.some((imageId) => imageId === id)) {
      newList = newList.filter((imageId) => imageId !== id);
    } else {
      newList.push(id);
    }

    projectDetailsImagesBl.setToggledImages(newList, id);
  };

  const handleBatchCheck = (id: string): void => {
    const { detailsImages, lastSelectedImageId } = imagesStore;
    const lastIndex = detailsImages.findIndex((i) => i.id === lastSelectedImageId);
    const markUpToIndex = detailsImages.findIndex((i) => i.id === id);

    if (lastIndex === undefined || markUpToIndex === undefined || lastIndex === -1 || markUpToIndex === -1) {
      handleNonBatchCheck(id);
      return;
    }

    const changeDirection = lastIndex < markUpToIndex ? 1 : -1;
    const comparison = lastIndex < markUpToIndex ? (k: number, n: number): boolean => k <= n : (k: number, n: number): boolean => k >= n;
    let toggledImages = imagesStore.toggledImages.slice();

    for (let i = lastIndex; comparison(i, markUpToIndex); i += changeDirection) {
      const image = detailsImages[i];

      if (image.isLocked) {
        continue;
      }

      const idToToggle = image.id;

      if (imagesStore.selectionMode === 'Select' && !toggledImages.includes(idToToggle)) {
        toggledImages.push(idToToggle);
      } else if (imagesStore.selectionMode === 'Deselect' && toggledImages.includes(idToToggle)) {
        toggledImages = toggledImages.filter((id) => id !== idToToggle);
      }
    }

    projectDetailsImagesBl.setToggledImages(toggledImages, lastSelectedImageId);
  };

  const handleThumbCheck = (id: string, isBatch: boolean): void => {
    const { lastSelectedImageId } = imagesStore;
    if (isBatch && lastSelectedImageId) {
      handleBatchCheck(id);
    } else {
      handleNonBatchCheck(id);
    }
  };

  const handleImageSetChange = async (id: string, type: ImageSetType): Promise<void> => {
    await projectDetailsImagesBl.updateProjectImageSetAsync(id, type);
  };

  const onCheckAll = (): void => {
    imageFilterService.changeSelectionMode();
  };

  const { filterId, imagesPaging, detailsImages, selectionMode, toggledImages, imageFilters } = imagesStore;
  imagesPaging.pageSize = settingsContext.store.projectDetailsImagesPageSize;

  const isLoading = overlayLoader.isSpinnerVisible('details-images-loading');

  let orderBy: string = '';
  let orderType: string = '';

  if (imageFilters.sorting.length === 1) {
    orderBy = imageFilters.sorting[0].by || '';
    orderType = imageFilters.sorting[0].direction || '';
  }

  return (
    <ProjectDetailsImagesList
      isLoading={isLoading}
      filterId={filterId}
      projectId={projectId}
      workspaceId={workspaceId}
      detailsImages={detailsImages}
      pagination={{
        pageNumber: imagesPaging.pageNumber,
        pageSize: settingsContext.store.projectDetailsImagesPageSize,
        totalCount: imagesPaging.totalCount,
        onChange: handlePaginationChange,
      }}
      viewMode={userStore.projectImageListViewMode}
      authToken={authStore.token}
      selectionMode={selectionMode}
      toggledImages={toggledImages}
      orderBy={orderBy}
      orderType={orderType}
      onCheck={handleThumbCheck}
      onOrderingChange={handleOrderingChangeAsync}
      onCheckAll={onCheckAll}
      onImageSetChange={handleImageSetChange}
    />
  );
});
